import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FilterOptionsState,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Field, FieldProps, FormikProps, getIn, useFormikContext } from 'formik';
import { DepartmentData } from '@shared/interfaces/DepartmentData';
import { useAllDepartments } from '@shared/hooks/useAllDepartments';
import { FlagFields } from '@modules/HappinessModule/interfaces/FlagFields';
import { FlagFormFields, FlagFormFieldsNewProjectAssignment } from '../../../constants/FlagFormFields';
import { Workloads } from '@shared/enums/Workloads';
import { parseDateForPicker, formatDateForSave } from '@shared/helpers/date';
import { DatePicker } from '@mui/x-date-pickers';
import { rockySwitcherStyles } from '@styles/rockySwitcherStyles';
import { useMyEvalData } from '@modules/EvaluationModule/pages/UserEvalPage/hooks/useMyEvalData';
import { EvaluationStatus } from '@modules/EvaluationModule/enums/EvaluationStatus';
import { NewProjectAssignmentNearestEval } from '@modules/HappinessModule/interfaces/NewProjectAssignmentData';
import { UserInfo } from '@shared/interfaces/UserInfo';
import { FlagFieldBlockPreview } from '@modules/HappinessModule/pages/FlagPage/pages/EditFlagFormPage/components/FlagPreview/components/FlagFieldBlockPreview';
import AddIcon from '@mui/icons-material/Add';
import { format, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { editFlagAction } from '@modules/HappinessModule/pages/FlagPage/pages/CreateFlagFormPage/redux/actions';
import { useParams } from 'react-router-dom';
import { mapNewProjectAssignmentDataToPayload } from '@modules/HappinessModule/pages/FlagPage/helpers/mapNewProjectAssignmentDataToPayload';
import { LoadingButton } from '@mui/lab';
import { addFlagDataStateSelector } from '@modules/HappinessModule/pages/FlagPage/pages/CreateFlagFormPage/redux/selectors';
import { DataState } from '@shared/enums/DataState';
import { displayNotification } from '@modules/App/redux/notifications/actions';
import { RocketLoader } from '@shared/components/RocketLoader';
import { getCurrentSeniority } from '@modules/HappinessModule/pages/FlagPage/pages/EditFlagFormPage/helpers/getFlagPageState';
import { RequestedOS, RequestedOSLabel } from '@shared/enums/RequestedOS';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  user: UserInfo;
  setIsNewHomeProjectFoundSetted: (value: boolean) => void;
}

export const NewHomeProjectFound: FC<Props> = ({ isOpen, handleClose, user, setIsNewHomeProjectFoundSetted }) => {
  const { flagId } = useParams();
  const userId = user.id;
  const rockySwitcher = rockySwitcherStyles();
  const allDepartments = useAllDepartments();
  const { values, setFieldValue, errors, initialValues } = useFormikContext<FlagFields>();
  const userEvals = useMyEvalData(userId);
  const isUserEvalsLoaded = userEvals.state === DataState.Fulfilled && userEvals.data?.user.id === userId;
  const isUserEvalsError = userEvals.state === DataState.Rejected;
  const userEval = userEvals.data?.history.find(
    (evalItem) => evalItem.status === EvaluationStatus.Ongoing || evalItem.status === EvaluationStatus.Upcoming
  );
  const hasNoEvaluations = isUserEvalsLoaded && !userEval;
  const dispatch = useDispatch();
  const [isEvalNotesShown, setIsEvalNotesShown] = useState(false);
  const [isTimeOffNotesShown, setIsTimeOffNotesShown] = useState(false);

  const { loadingState } = useSelector(addFlagDataStateSelector);
  const isPatching = loadingState === DataState.Pending;

  const isCompensationAdjustmentEditable = !initialValues.newProjectAssignment;

  const handleCloseWithReset = () => {
    handleClose();
    setFieldValue(FlagFormFields.newHomeResolution.name, initialValues.newHomeResolution);
    setFieldValue(FlagFormFields.newProjectAssignment.name, null);
  };

  const startDateError = getIn(errors, FlagFormFieldsNewProjectAssignment.startDate.name);
  const isSavingDisabled =
    !values.newProjectAssignment?.project ||
    !values.newProjectAssignment?.startDate ||
    !values.newProjectAssignment?.nearestEval ||
    Object.values(errors.newProjectAssignment ?? {}).length > 0;

  const saveFlagWithNewProjectFound = () => {
    if (!flagId || !values.newProjectAssignment?.nearestEval) return;
    dispatch(
      editFlagAction({
        fields: {
          nhEmailSentStatus: values.nhEmailSentStatus,
          isConfidential: values.isConfidential,
          isNewHomeNeeded: values.isNewHomeNeeded,
          pros: values.pros,
          cons: values.cons,
          softSkills: values.softSkills,
          newHomeResolution: values.newHomeResolution,
          numSubmissions: values.numSubmissions,
          numTICIFailed: values.numTICIFailed,
          numOptionsShown: values.numOptionsShown,
          newProjectAssignment: mapNewProjectAssignmentDataToPayload(values.newProjectAssignment),
        },
        flagId,
        isNoNavigation: true,
      })
    );
  };

  useEffect(() => {
    const nearestEval: NewProjectAssignmentNearestEval | null = userEval
      ? {
          date: userEval.date,
          status: userEval.status,
          id: userEval.evaluationId,
        }
      : null;
    setFieldValue(FlagFormFieldsNewProjectAssignment.workload.name, Workloads.FullTime);
    setFieldValue(FlagFormFieldsNewProjectAssignment.isSowSigned.name, true);
    setFieldValue(FlagFormFieldsNewProjectAssignment.nearestEval.name, nearestEval);
    setFieldValue(FlagFormFieldsNewProjectAssignment.currentSeniority.name, user.seniority);
    setFieldValue(FlagFormFieldsNewProjectAssignment.addHRSupport.name, false);
  }, [userEval]);

  useEffect(() => {
    if (!isPatching && loadingState === DataState.Fulfilled) {
      setIsNewHomeProjectFoundSetted(true);
      dispatch(displayNotification('Flag was successfully updated'));
      handleClose();
    }
  }, [isPatching]);

  const isAllNesessaryDataLoaded = isUserEvalsLoaded || isUserEvalsError;

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      scroll="body"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '524px',
          },
        },
      }}
      open={isOpen}
      onClose={handleCloseWithReset}
    >
      {!isAllNesessaryDataLoaded ? (
        <Box sx={{ margin: '20%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <RocketLoader />
        </Box>
      ) : (
        <>
          <DialogTitle
            sx={{
              marginTop: '20px',
            }}
          >
            New project found
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseWithReset}
            sx={{
              position: 'absolute',
              right: 8,
              top: 28,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            {(isUserEvalsError || hasNoEvaluations) && (
              <Alert severity={isUserEvalsError ? 'error' : 'warning'} sx={{ mb: 2 }}>
                {isUserEvalsError
                  ? 'Failed to load evaluation data. Please try again later.'
                  : 'No ongoing or upcoming evaluation found for this user. An evaluation is required to save the new project assignment.'}
              </Alert>
            )}
            <Box>
              <Typography variant="subtitle1">Project information</Typography>
              <Stack
                sx={{
                  mt: '16px',
                  rowGap: '16px',
                }}
              >
                <Field
                  name={FlagFormFieldsNewProjectAssignment.project.name}
                  component={Autocomplete}
                  getOptionLabel={(option: DepartmentData) => option.name}
                  filterOptions={(options: DepartmentData[], state: FilterOptionsState<DepartmentData>) => {
                    const searchString = String(state.inputValue).toLowerCase();
                    return options.filter((option) => option.name.toLowerCase().includes(searchString));
                  }}
                  disableClearable
                  value={values.newProjectAssignment?.project}
                  onChange={(_: SyntheticEvent, newValue: DepartmentData) =>
                    setFieldValue(FlagFormFieldsNewProjectAssignment.project.name, newValue)
                  }
                  options={allDepartments.data ?? []}
                  renderInput={(params: FormikProps<TextFieldProps>) => (
                    <TextField
                      {...params}
                      label={FlagFormFieldsNewProjectAssignment.project.label}
                      placeholder="Please select"
                    />
                  )}
                />

                <Grid container columnSpacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      label={FlagFormFieldsNewProjectAssignment.startDate.label}
                      views={['year', 'month', 'day']}
                      openTo="month"
                      inputFormat="dd MMM yyyy"
                      value={parseDateForPicker(values.newProjectAssignment?.startDate ?? null)}
                      disableMaskedInput
                      onChange={(newDate: unknown) => {
                        setFieldValue(
                          FlagFormFieldsNewProjectAssignment.startDate.name,
                          formatDateForSave(newDate),
                          true
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onKeyDown={(e) => e.preventDefault()}
                          sx={{
                            caretColor: 'transparent',
                          }}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'Select date',
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          variant="outlined"
                          fullWidth
                          helperText={startDateError}
                          error={startDateError}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      type="select"
                      name={FlagFormFieldsNewProjectAssignment.workload.name}
                      component={({ field }: FieldProps) => (
                        <FormControl fullWidth>
                          <InputLabel>{FlagFormFieldsNewProjectAssignment.workload.label}</InputLabel>
                          <Select
                            variant="outlined"
                            label={FlagFormFieldsNewProjectAssignment.workload.label}
                            {...field}
                          >
                            {Object.values(Workloads).map((workload) => (
                              <MenuItem key={workload} value={workload}>
                                {workload}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                <Field
                  type="select"
                  name={FlagFormFieldsNewProjectAssignment.requestedOS.name}
                  component={({ field }: FieldProps) => (
                    <FormControl fullWidth>
                      <InputLabel>{FlagFormFieldsNewProjectAssignment.requestedOS.label}</InputLabel>
                      <Select
                        variant="outlined"
                        label={FlagFormFieldsNewProjectAssignment.requestedOS.label}
                        {...field}
                      >
                        {Object.values(RequestedOS).map((requestedOS) => (
                          <MenuItem key={requestedOS} value={requestedOS}>
                            {RequestedOSLabel[requestedOS]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />

                <Field name={FlagFormFieldsNewProjectAssignment.requestedOSComment.name}>
                  {({ field }: FieldProps) => (
                    <TextField
                      error={!!errors.requestedOSComment}
                      disabled={!values.newProjectAssignment?.requestedOS}
                      size="medium"
                      label={FlagFormFieldsNewProjectAssignment.requestedOSComment.label}
                      variant="outlined"
                      fullWidth
                      {...field}
                    />
                  )}
                </Field>

                <Field
                  name={FlagFormFieldsNewProjectAssignment.isSowSigned.name}
                  type="checkbox"
                  component={({ field }: FieldProps) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          className={rockySwitcher.switcher}
                          checked={values.newProjectAssignment?.isSowSigned}
                          onChange={(_, checked) =>
                            setFieldValue(FlagFormFieldsNewProjectAssignment.isSowSigned.name, checked)
                          }
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle1"
                          sx={{
                            marginLeft: '10px',
                          }}
                        >
                          {FlagFormFieldsNewProjectAssignment.isSowSigned.label}
                        </Typography>
                      }
                    />
                  )}
                />
              </Stack>
            </Box>
            <Box
              sx={{
                marginTop: '24px',
              }}
            >
              <Typography variant="subtitle1">Compensation and time off</Typography>
              <Stack
                sx={{
                  mt: '16px',
                }}
                rowGap="16px"
              >
                <Stack direction="row" alignItems="center" columnGap="16px">
                  <FlagFieldBlockPreview
                    label={FlagFormFieldsNewProjectAssignment.currentSeniority.label}
                    value={getCurrentSeniority(values.newProjectAssignment?.currentSeniority ?? null)}
                  />
                  {isUserEvalsLoaded && values.newProjectAssignment?.nearestEval?.date && (
                    <FlagFieldBlockPreview
                      label={FlagFormFieldsNewProjectAssignment.nearestEval.label}
                      value={
                        values.newProjectAssignment?.nearestEval?.status === EvaluationStatus.Ongoing
                          ? 'Ongoing'
                          : format(parseISO(values.newProjectAssignment.nearestEval.date), 'dd MMM yyyy')
                      }
                    />
                  )}
                </Stack>
                {isCompensationAdjustmentEditable && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!values.newProjectAssignment?.compensationAdjustmentAt}
                        onChange={(_, checked) =>
                          setFieldValue(
                            FlagFormFieldsNewProjectAssignment.compensationAdjustmentAt.name,
                            checked ? new Date().toISOString() : null
                          )
                        }
                      />
                    }
                    label={
                      <Stack>
                        <Typography variant="subtitle1">
                          {FlagFormFieldsNewProjectAssignment.compensationAdjustmentAt.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {FlagFormFieldsNewProjectAssignment.compensationAdjustmentAt.helperText}
                        </Typography>
                      </Stack>
                    }
                    sx={{ alignItems: 'flex-start', marginLeft: 0 }}
                  />
                )}
                {isEvalNotesShown ? (
                  <Field name={FlagFormFieldsNewProjectAssignment.evaluationNotes.name}>
                    {({ field }: FieldProps) => (
                      <TextField
                        size="medium"
                        label={FlagFormFieldsNewProjectAssignment.evaluationNotes.label}
                        multiline
                        minRows={2}
                        variant="outlined"
                        fullWidth
                        {...field}
                        value={field.value ?? ''}
                      />
                    )}
                  </Field>
                ) : (
                  <Link
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => setIsEvalNotesShown(true)}
                  >
                    <AddIcon sx={{ marginRight: '4px' }} /> Add notes about evaluation and promotion
                  </Link>
                )}
                {isTimeOffNotesShown ? (
                  <Field name={FlagFormFieldsNewProjectAssignment.timeOffNotes.name}>
                    {({ field }: FieldProps) => (
                      <TextField
                        size="medium"
                        label={FlagFormFieldsNewProjectAssignment.timeOffNotes.label}
                        multiline
                        minRows={2}
                        variant="outlined"
                        fullWidth
                        {...field}
                        value={field.value ?? ''}
                      />
                    )}
                  </Field>
                ) : (
                  <Link
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => setIsTimeOffNotesShown(true)}
                  >
                    <AddIcon sx={{ marginRight: '4px' }} /> Add notes about time off
                  </Link>
                )}
              </Stack>
            </Box>
            <Stack
              rowGap="7px"
              sx={{
                mt: '14px',
              }}
            >
              <Alert icon={<EmailOutlinedIcon color="disabled" />} severity="info">
                <Typography variant="body2" fontSize="14px">
                  An email with these details will be sent to new DM with a copy to PDM, Talent Operations, Background
                  Check, and IT Support
                </Typography>
                <Field
                  name={FlagFormFieldsNewProjectAssignment.addHRSupport.name}
                  type="checkbox"
                  component={({ field }: FieldProps) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          className={rockySwitcher.switcher}
                          checked={values.newProjectAssignment?.addHRSupport}
                          onChange={(_, checked) =>
                            setFieldValue(FlagFormFieldsNewProjectAssignment.addHRSupport.name, checked)
                          }
                          sx={{ marginTop: '7px' }}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle1"
                          sx={{
                            marginLeft: '10px',
                            marginTop: '7px',
                          }}
                        >
                          {FlagFormFieldsNewProjectAssignment.addHRSupport.label}
                        </Typography>
                      }
                    />
                  )}
                />
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={handleCloseWithReset}>
              Cancel
            </Button>
            <LoadingButton
              loading={isPatching}
              variant="contained"
              color="primary"
              onClick={saveFlagWithNewProjectFound}
              disabled={isSavingDisabled || isPatching}
            >
              Save and send
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
